import React, { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { createTollTag, updateTollTag, deleteTollTag } from "@/lib/api";
import { TruckSelect } from "./TruckSelect";
import { SearchBar } from "./SearchBar";
import { AddAlertDialog } from "./AddAlertDialog";
import { errMsg, normPlate, selectCls } from "./utils";

const STATUS = { actif: "Actif", perdu: "Perdu", hors_service: "Hors service" };

const TagRow = ({ tag, trucks, onPatch, onDelete }) => {
  const [notes, setNotes] = useState(tag.notes || "");
  const dim = tag.status !== "actif";
  return (
    <div
      className={`grid grid-cols-[70px_1fr_auto] md:grid-cols-[90px_1fr_150px_1fr_auto] gap-3 items-center border px-4 py-3 bg-white ${dim ? "border-red-300 bg-red-50/40" : "border-zinc-300"}`}
      data-testid={`toll-tag-row-${tag.number}`}
    >
      <div className="mono text-2xl font-bold">N°{tag.number}</div>
      <TruckSelect
        trucks={trucks}
        value={tag.assigned_truck_id}
        onChange={(v) => onPatch(tag, { assigned_truck_id: v })}
        testId={`toll-tag-truck-${tag.number}`}
      />
      <select
        value={tag.status}
        onChange={(e) => onPatch(tag, { status: e.target.value })}
        className={`${selectCls} hidden md:block`}
        data-testid={`toll-tag-status-${tag.number}`}
      >
        {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <Input
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        onBlur={() => notes !== (tag.notes || "") && onPatch(tag, { notes })}
        placeholder="Notes…"
        className="rounded-none border-zinc-300 h-10 hidden md:block"
        data-testid={`toll-tag-notes-${tag.number}`}
      />
      <button
        onClick={() => onDelete(tag)}
        className="p-2 text-zinc-400 hover:text-red-600 transition-colors"
        data-testid={`toll-tag-delete-${tag.number}`}
        aria-label="Supprimer"
      >
        <Trash size={18} weight="bold" />
      </button>
    </div>
  );
};

export const TagsManager = ({ tags, trucks, reload }) => {
  const [number, setNumber] = useState("");
  const [truckId, setTruckId] = useState(null);
  const [alert, setAlert] = useState(null);
  const [q, setQ] = useState("");
  const plateOf = (id) => trucks.find((t) => t.id === id)?.license_plate || "";

  const save = async () => {
    try {
      const t = await createTollTag({ number: number.trim(), assigned_truck_id: truckId });
      setNumber("");
      setTruckId(null);
      toast.success(`Télépéage N°${t.number} ajouté`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const add = () => {
    const n = number.trim();
    if (!n) return;
    const dup = tags.find((t) => t.number.trim().toLowerCase() === n.toLowerCase());
    if (dup) return setAlert({ type: "duplicate", number: dup.number, plate: plateOf(dup.assigned_truck_id) });
    const busy = truckId && tags.find((t) => t.assigned_truck_id === truckId);
    if (busy) return setAlert({ type: "truck", number: n, existing: busy.number, plate: plateOf(truckId) });
    save();
  };

  const nq = normPlate(q);
  const shown = nq
    ? tags.filter((t) => normPlate(t.number).includes(nq) || normPlate(plateOf(t.assigned_truck_id)).includes(nq))
    : tags;

  const patch = async (tag, data) => {
    const prev = data.assigned_truck_id ? tags.find((t) => t.assigned_truck_id === data.assigned_truck_id && t.id !== tag.id) : null;
    try {
      await updateTollTag(tag.id, data);
      if (prev) toast.info(`Le N°${prev.number} a été retiré de ce camion`);
      else toast.success("Enregistré");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (tag) => {
    if (!window.confirm(`Supprimer le télépéage N°${tag.number} ?`)) return;
    try {
      await deleteTollTag(tag.id);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="toll-tags-manager">
      <div className="border-2 border-zinc-900 bg-white p-4 mb-6 flex flex-wrap gap-2 items-center">
        <Input
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="N° du badge (ex : 64)"
          className="rounded-none border-zinc-900 h-10 w-48"
          data-testid="toll-tag-number-input"
        />
        <div className="flex-1 min-w-[200px]">
          <TruckSelect trucks={trucks} value={truckId} onChange={setTruckId} testId="toll-tag-new-truck" />
        </div>
        <Button onClick={add} className="rounded-none bg-zinc-900 hover:bg-zinc-700 h-10 gap-1" data-testid="toll-tag-add-btn">
          <Plus size={14} weight="bold" /> Ajouter
        </Button>
      </div>
      <SearchBar value={q} onChange={setQ} placeholder="Rechercher un N° ou une plaque…" testId="toll-tags-search" />
      <div className="space-y-2">
        {tags.length === 0 && <div className="text-sm text-zinc-500" data-testid="toll-tags-empty">Aucun télépéage enregistré.</div>}
        {tags.length > 0 && shown.length === 0 && <div className="text-sm text-zinc-500" data-testid="toll-tags-no-result">Aucun résultat.</div>}
        {shown.map((t) => (
          <TagRow key={`${t.id}-${t.updated_at}`} tag={t} trucks={trucks} onPatch={patch} onDelete={remove} />
        ))}
      </div>
      <AddAlertDialog alert={alert} onClose={() => setAlert(null)} onConfirm={save} />
    </div>
  );
};
